import React from 'react'
import { t } from '@ulule/localize'
import * as models from '@owl-nest/models'
import differenceInHours from 'date-fns-legacy/difference_in_hours'
import differenceInMinutes from 'date-fns-legacy/difference_in_minutes'

import InteractiveCountdown from '../components/InteractiveCountdown/InteractiveCountdown.js'
import get from './model.js'
import userModel from './user.js'

function getUrl(project, urlName) {
  return project.urls.web[urlName]
}

function isPresale(project) {
  return project.type === 'presale' ? true : false
}

function isFinished(project) {
  return project.finished && !project.is_in_extra_time
}

function isOkpalOffboardingAvailable(project) {
  return project.currency && (project.currency === 'EUR' || project.currency === 'CAD')
}

function timeLeft(project) {
  if (!project.is_online) return

  if (project.date_end === null) return

  if (project.date_end) {
    const delta = project.date_end_extra_time
      ? +new Date(project.date_end_extra_time) - +new Date()
      : +new Date(project.date_end) - +new Date()

    if (delta > 48 * 3600000) {
      return t('days left')
    }

    if (delta >= 3600000) {
      return t('hours left')
    }

    if (delta === 3600000) {
      return t('hour left')
    }

    if (delta > 60000) {
      return t('minutes left')
    }

    if (delta === 60000) {
      return t('minute left')
    }

    if (delta === 1) {
      return t('second left')
    }

    return t('seconds left')
  }

  return
}

function getCountdown(project) {
  if (project.date_end === null) return

  const timestamp = project.date_end_extra_time ? +new Date(project.date_end_extra_time) : +new Date(project.date_end)
  const currentTimestamp = +new Date()
  const diff = timestamp - currentTimestamp
  let countdown

  if (diff > 48 * 3600000) {
    // > 48h
    const lastDays = Math.ceil(diff / (24 * 3600000))
    countdown = lastDays
    return countdown
  }

  if (diff >= 3600000) {
    // > 1h // 60 * 60 * 1 * 1000
    const lastHours = Math.round(diff / 3600000)
    countdown = lastHours

    return countdown
  }

  if (diff >= 60000) {
    // >= 60s //60 * 1 * 1000
    const lastMinutes = Math.round(diff / 60000)
    countdown = lastMinutes

    return countdown
  }

  // seconds
  const lastSeconds = Math.round(diff / 1000)
  countdown = lastSeconds

  return countdown
}

function success(project) {
  if (isFinished(project) && project.percent >= 100) {
    return true
  }
  return false
}

function failed(project) {
  if (isFinished(project) && project.percent < 100) {
    return true
  }
  return false
}

function funded(project) {
  if (isFinished(project) === false && project.percent >= 100) {
    return true
  }
  return false
}

function amountLeft(project) {
  if (isPresale) {
    return project.goal - project.comitted
  }

  if (project.goal) {
    const result = project.goal - (project.amount_raised || 0)

    if (result > 0) {
      return result
    }
  }
  return 0
}

function getShippingPrices(project) {
  const projectShippings = project && project.delivery && project.delivery.shippings

  const projectShippingsPrices = projectShippings && projectShippings.map((shipping) => shipping.amount)

  return projectShippingsPrices
}

function getMinShipping(project) {
  let projectShippings = getShippingPrices(project) || []
  const internationalShipping = project && project.delivery && project.delivery.shipping_int
  const nationalShipping = project && project.delivery && project.delivery.shipping_nat

  if (internationalShipping || internationalShipping === 0) {
    projectShippings.push(internationalShipping)
  }
  if (nationalShipping || nationalShipping === 0) {
    projectShippings.push(nationalShipping)
  }
  return projectShippings.length > 0 ? Math.min(...projectShippings) : null
}

function getMaxShipping(project) {
  let projectShippings = getShippingPrices(project) || []
  const internationalShipping = project && project.delivery && project.delivery.shipping_int
  const nationalShipping = project && project.delivery && project.delivery.shipping_nat

  if (internationalShipping || internationalShipping === 0) {
    projectShippings.push(internationalShipping)
  }
  if (nationalShipping || nationalShipping === 0) {
    projectShippings.push(nationalShipping)
  }

  return projectShippings.length > 0 ? Math.max(...projectShippings) : null
}

export const isOrdersEnabled = (project) => {
  return project.orders_enabled !== undefined ? project.orders_enabled : true
}

/* Returns the project main image at a given size */
export const getMainImage = (project, size = 'large') => {
  if (project && project.main_image && project.main_image.versions && project.main_image.versions[size]) {
    return project.main_image.versions[size].url
  }
  return ''
}

export const getProgress = (project) => {
  const value = project.type === 'presale' ? project.nb_products_sold : project.amount_raised

  return Math.floor((value * 100) / project.goal)
}

/* Returns left time in milliseconds */
export const getTimeleft = (startDate, endDate) => {
  const firstDate = new Date(startDate)
  const secondDate = new Date(endDate)

  return secondDate.getTime() - firstDate.getTime()
}

export const getFinishTrad = (endDate) => {
  const secondDate = new Date(endDate)
  const localDate = new Date()
  const timePassed = differenceInHours(localDate, secondDate)
  const minutePassed = differenceInMinutes(localDate, secondDate)

  if (timePassed === 0) {
    return t('Just ended %(hours)d min ago!', { hours: minutePassed })
  }

  if (timePassed < 12 && timePassed > 0) {
    return t('Just ended %(hours)dh ago!', { hours: timePassed })
  }

  return t('Finished')
}

/* Returns text for countdown */
export const getShortCountdown = (project) => {
  return models.project.getCountDown(project, true)
}

export const rawProgress = (project) => {
  const value = project.type === 'presale' ? project.nb_products_sold : project.amount_raised
  return (value * 100) / project.goal
}

export const progress = (project) => {
  return Math.floor(rawProgress(project))
}

export const project = (currLang) => (project) => {
  const validator = project && project.proposal && project.proposal.validator
  const owner = project && project.owner
  const manager = project && project.manager
  const delivery = project && project.delivery

  if (currLang === undefined || currLang === '') currLang = 'en'

  const projectProps = {
    props: {
      ...project,
      getMainImage: (size) => getMainImage(project, size),
      getUrl: (project, urlName) => getUrl(project, urlName),
      percent: progress(project),
      isPresale: isPresale(project),
      isFinished: isFinished(project),
      isFunded: funded(project),
      isSuccess: success(project),
      isFailed: failed(project),
      timeLeft: timeLeft(project),
      countdown: getCountdown(project),
      amountLeft: amountLeft(project),
      isOkpalOffboardingAvailable: isOkpalOffboardingAvailable(project),
      isOrdersEnabled: isOrdersEnabled(project),
      delivery: {
        ...delivery,
        minimalShipping: getMinShipping(project),
        maximalShipping: getMaxShipping(project),
        shippingsPrices: getShippingPrices(project),
      },
    },
    user: owner && userModel(owner),
    manager: manager && userModel(manager),
    validator: validator !== null ? userModel(validator) : {},
  }
  projectProps.get = get(projectProps, currLang, project.lang)

  return projectProps
}

export const getComingSoonLabel = (project, isStaff = true) => {
  if (isStaff || (project.user_role && ['editor', 'moderator', 'owner'].includes(project.user_role))) {
    return project.status === 'validated' ? t('Ready to go online') : t('In progress')
  }
  return t('Online shortly, sign up now!')
}

export function getStatus({ extraTime, finished, endDate, isOnline }) {
  if (extraTime) {
    return t('Extension')
  } else if (finished) {
    return t('Finished')
  } else if (isOnline && !endDate) {
    return ''
  }
}
